/* ============================================
   ChatImage — miniatura de imagen dentro de una burbuja
   Encuadra como WhatsApp: caja acotada + recorte centrado.
   Sin esto un pantallazo de celular (9:19) se renderiza de
   600px de alto y se come toda la ventana de mensajes.
   La imagen completa se ve en el lightbox al hacer click.
   ============================================ */
import { useState } from 'react'

/** Caja maxima de la miniatura, en px. Ancha pero baja, como WhatsApp Web. */
const MAX_W = 300
const MAX_H = 340
/** Relacion ancho/alto permitida. Fuera de este rango se recorta en vez de
    adelgazar (vertical) o achatar (panoramica) la miniatura. */
const MIN_RATIO = 0.75
const MAX_RATIO = 2.4

export interface ThumbBox { width: number; height: number }

/** Calcula la caja visible a partir del tamano natural de la imagen.
    Nunca agranda: una imagen chica se muestra a su tamano real. */
export function thumbBox(natW: number, natH: number): ThumbBox {
  if (!natW || !natH) return { width: MAX_W, height: MAX_H }
  const ratio = Math.min(Math.max(natW / natH, MIN_RATIO), MAX_RATIO)
  let width = Math.min(natW, MAX_W)
  let height = width / ratio
  if (height > MAX_H) {
    height = MAX_H
    width = Math.min(MAX_H * ratio, natW, MAX_W)
  }
  return { width: Math.round(width), height: Math.round(height) }
}

interface ChatImageProps {
  src: string
  alt?: string
  onClick?: () => void
}

export default function ChatImage({ src, alt = 'Imagen', onClick }: ChatImageProps) {
  const [box, setBox] = useState<ThumbBox | null>(null)

  return (
    /* La caja lleva el tamano y la relacion de aspecto; la imagen solo la
       rellena. Asi, si la burbuja es mas angosta que la caja (ventana chica,
       panel de medios abierto), la caja se achica entera con `max-width:100%`
       en vez de que la imagen se escape de la burbuja. */
    <div
      className="chat-bubble__image-box"
      /* Si el click cierra un arrastre de seleccion (venia marcando el texto de
         arriba y solto sobre la imagen) no abrimos el lightbox: se perderia la
         seleccion recien hecha. Un click limpio siempre llega sin seleccion,
         porque el mousedown previo la deshace. */
      onClick={() => {
        if (window.getSelection()?.toString()) return
        onClick?.()
      }}
      style={box ? { width: box.width, aspectRatio: `${box.width} / ${box.height}` } : undefined}
    >
      <img
        src={src}
        alt={alt}
        className="chat-bubble__image"
        loading="lazy"
        title="Click para ver la imagen completa"
        onLoad={(e) => {
          const img = e.currentTarget
          setBox(thumbBox(img.naturalWidth, img.naturalHeight))
        }}
      />
    </div>
  )
}
